// Single shape presets (basic shapes first, taught earliest)
export * from './single-triangle.preset';
export * from './single-right-triangle.preset';
export * from './single-rectangle.preset';
export * from './single-square.preset';
export * from './single-circle.preset';

// Feature presets
export * from './triangle-features.preset';
export * from './rectangle-features.preset';
export * from './square-features.preset';
export * from './parallelogram-features.preset';
export * from './rhombus-features.preset';
export * from './trapezoid-features.preset';
export * from './feature-ports.preset';

// Arc presets (arcs/sectors typically taught after basic shapes & circles)
export * from './arc.preset';

// Dependency presets
export * from './internal-dependency.preset';
export * from './scalar-dependency.preset';

// Positional param presets
export * from './shared-point.preset';
export * from './shared-edge.preset';
export * from './point-on-edge.preset';
export * from './triangle-in-rectangle.preset';

// Complex presets
export * from './composite-figure.preset';
export * from './similarity-chain.preset';
export * from './pythagoras.preset';

// Control presets (sliders, buttons, tables)
export * from './slider-sum.preset';
export * from './trig-buttons.preset';
export * from './trig-table.preset';

// Label mode presets
export * from './label-modes.preset';

// Annotation presets
export * from './annotations.preset';

// Canvas selection presets
export * from './test-canvas-selection.preset';
export * from './right-angle-explanation.preset';

import type { GeometryPadWidgetConfig } from '../geometrypad.model';

import {
  ARC_BASICS_PRESET,
  ARC_SECTOR_SEGMENT_PRESET,
  ARC_LENGTH_FORMULA_PRESET,
  ARC_SECTOR_AREA_PRESET,
  ARC_CHORD_RELATIONSHIP_PRESET,
  SINGLE_ARC_PRESET,
} from './arc.preset';
import { SINGLE_TRIANGLE_PRESET } from './single-triangle.preset';
import { SINGLE_RIGHT_TRIANGLE_PRESET } from './single-right-triangle.preset';
import { SINGLE_RECTANGLE_PRESET } from './single-rectangle.preset';
import { SINGLE_SQUARE_PRESET } from './single-square.preset';
import { SINGLE_CIRCLE_PRESET } from './single-circle.preset';
import { INTERNAL_DEPENDENCY_PRESET } from './internal-dependency.preset';
import { SCALAR_DEPENDENCY_PRESET } from './scalar-dependency.preset';
import { SHARED_POINT_PRESET } from './shared-point.preset';
import { SHARED_EDGE_PRESET } from './shared-edge.preset';
import { POINT_ON_EDGE_PRESET } from './point-on-edge.preset';
import { TRIANGLE_IN_RECTANGLE_PRESET } from './triangle-in-rectangle.preset';
import { COMPOSITE_FIGURE_PRESET } from './composite-figure.preset';
import { SIMILARITY_CHAIN_PRESET } from './similarity-chain.preset';
import { PYTHAGORAS_PRESET } from './pythagoras.preset';
import { SLIDER_SUM_PRESET } from './slider-sum.preset';
import { TRIG_BUTTONS_PRESET } from './trig-buttons.preset';
import { TRIG_TABLE_PRESET } from './trig-table.preset';
import {
  LABEL_GIVEN_ONLY_PRESET,
  LABEL_WORKED_EXAMPLE_PRESET,
  LABEL_FULL_SOLUTION_PRESET,
  LABEL_SYMBOLIC_PRESET,
  LABEL_UNLABELED_PRESET,
} from './label-modes.preset';
import { TRIANGLE_FEATURES_PRESET } from './triangle-features.preset';
import { RECTANGLE_FEATURES_PRESET } from './rectangle-features.preset';
import { SQUARE_FEATURES_PRESET } from './square-features.preset';
import { PARALLELOGRAM_FEATURES_PRESET } from './parallelogram-features.preset';
import { RHOMBUS_FEATURES_PRESET } from './rhombus-features.preset';
import { TRAPEZOID_FEATURES_PRESET } from './trapezoid-features.preset';
import { FEATURE_PORTS_PRESET } from './feature-ports.preset';
import { ANNOTATIONS_PRESET } from './annotations.preset';
import {
  TEST_CANVAS_SELECTION_TRIANGLE_PRESET,
  TEST_CANVAS_SELECTION_MIXED_PRESET,
} from './test-canvas-selection.preset';
import { RIGHT_ANGLE_EXPLANATION_PRESET } from './right-angle-explanation.preset';

/**
 * All available GeometryPad presets.
 */
export const GEOMETRYPAD_PRESETS: GeometryPadWidgetConfig[] = [
  // Priority presets
  RIGHT_ANGLE_EXPLANATION_PRESET,
  TRIG_TABLE_PRESET,
  ANNOTATIONS_PRESET,

  // Single shape presets (basic shapes — taught first)
  SINGLE_TRIANGLE_PRESET,
  SINGLE_RIGHT_TRIANGLE_PRESET,
  SINGLE_RECTANGLE_PRESET,
  SINGLE_SQUARE_PRESET,
  SINGLE_CIRCLE_PRESET,

  // Feature presets (shape properties & features)
  TRIANGLE_FEATURES_PRESET,
  RECTANGLE_FEATURES_PRESET,
  SQUARE_FEATURES_PRESET,
  PARALLELOGRAM_FEATURES_PRESET,
  RHOMBUS_FEATURES_PRESET,
  TRAPEZOID_FEATURES_PRESET,
  FEATURE_PORTS_PRESET,

  // Arc presets (arcs/sectors — taught after basic shapes & circles)
  SINGLE_ARC_PRESET,
  ARC_BASICS_PRESET,
  ARC_SECTOR_SEGMENT_PRESET,
  ARC_LENGTH_FORMULA_PRESET,
  ARC_SECTOR_AREA_PRESET,
  ARC_CHORD_RELATIONSHIP_PRESET,

  // Complex presets (Pythagoras, similarity — intermediate topics)
  COMPOSITE_FIGURE_PRESET,
  SIMILARITY_CHAIN_PRESET,
  PYTHAGORAS_PRESET,

  // Dependency presets
  INTERNAL_DEPENDENCY_PRESET,
  SCALAR_DEPENDENCY_PRESET,

  // Positional param presets
  SHARED_POINT_PRESET,
  SHARED_EDGE_PRESET,
  POINT_ON_EDGE_PRESET,
  TRIANGLE_IN_RECTANGLE_PRESET,

  // Control presets (sliders, buttons)
  SLIDER_SUM_PRESET,
  TRIG_BUTTONS_PRESET,

  // Label mode presets
  LABEL_GIVEN_ONLY_PRESET,
  LABEL_WORKED_EXAMPLE_PRESET,
  LABEL_FULL_SOLUTION_PRESET,
  LABEL_SYMBOLIC_PRESET,
  LABEL_UNLABELED_PRESET,

  // Canvas selection presets
  TEST_CANVAS_SELECTION_TRIANGLE_PRESET,
  TEST_CANVAS_SELECTION_MIXED_PRESET,
];

export type GeometrypadPresetId = (typeof GEOMETRYPAD_PRESETS)[number]['id'];
